import {AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, ViewChildren} from '@angular/core';
import {CommonModule} from '@angular/common';

import {AccordionComponent} from "../accordion";
import {DropdownFilterComponent} from "../dropdown-filter";
import {DatePickerComponent} from "../date-picker";
import {ToggleComponent} from "../toggler";
import {TaggerComponent} from "../tagger";
import {ButtonComponent} from "../button";

import {
  BatchQuickEdit,
  BatchRpcService,
  DocumentRpcService,
  DocumentType,
  QueueItem,
  QueueRpcService,
  QueueType,
  QuickEditType,
  Status,
  MessageService,
  MessageType,
  AbiType, TablePageOptions
} from "@alpha/services";
import {Claim, Helpers, OnChangePayload, Role, TypeEnum} from "../helpers";
import {OnChange, OnChangeType} from "./index";
import {TagDropdownComponent} from "../tag-dropdown/tag-dropdown.component";
import {uuid} from "@tinymce/tinymce-angular/utils/Utils";
import {FormsModule} from "@angular/forms";

interface DocumentTypePreset {
  id: string;
  name: string;
  types: DocumentType[]
}

@Component({
  selector: 'quick-edit',
  standalone: true,
  imports: [CommonModule, AccordionComponent, DropdownFilterComponent, DatePickerComponent, ToggleComponent, TaggerComponent, ButtonComponent, TagDropdownComponent, FormsModule],
  templateUrl: './quick-edit.component.html',
  styleUrls: ['./quick-edit.component.scss']
})
export class QuickEditComponent implements AfterViewInit {
  userRole: Role;

  constructor(private bs: BatchRpcService, private ds: DocumentRpcService, private qs: QueueRpcService, private ms: MessageService) {
    this.userRole = Helpers.getClaim(Claim.Role);
  }

  @Output() change = new EventEmitter<OnChange>()

  @Input() selection: string[] = [];

  @Input() toggle: boolean = false;

  @Input() filter: any = {};

  @ViewChildren(AccordionComponent) accordions: AccordionComponent[] | undefined;
  @ViewChild(DropdownFilterComponent) status_dropdown: DropdownFilterComponent | undefined;
  @ViewChild(DatePickerComponent) eta_datepicker: DatePickerComponent | undefined;
  @ViewChild(ToggleComponent) pickup_toggle: ToggleComponent | undefined;
  @ViewChildren(TagDropdownComponent) document_dropdown: TagDropdownComponent[] | undefined;

  status: Status[] = [];

  document_types: DocumentType[] = [];

  document_prefix: { name: string, value: string }[] = [
    {name: 'AWB/BOL Number', value: 'awb_bol_number'},
    {name: 'Reference Number', value: 'reference_number'},
    {name: 'Container Number', value: 'container_number'},
  ];


  selected_document_types: DocumentType[] = [];
  selected_document_prefix: { name: string, value: string }[] = [];


  document_request: { name: string, value: AbiType }[] = [
    {name: 'Print Entry Summary (7501)', value: AbiType.PrintEntrySummary},
    {name: 'Print Cargo Release (3461)', value: AbiType.PrintCargoReRelease},
    {name: 'Print Delivery Order', value: AbiType.PrintDeliveryOrder},
    {name: 'Query AWB & BOL Manifest', value: AbiType.Manifest},
    {name: 'Print Inbound 7512 (Coming Soon)', value: AbiType.PrintInbound7512},
    {name: 'Print ISF Confirmation (Coming Soon)', value: AbiType.PrintIsfDocument},
  ];

  selected_document_request: { name: string, value: AbiType }[] = [];


  tags: string[] | any = [];

  selected_status: Status | undefined;

  eta: string | undefined;

  toggle_shipped: boolean | undefined;

  toggle_shipped_touched: boolean = false;

  document_type_presets: DocumentTypePreset[] = [
  ]
  selected_document_type_preset: DocumentTypePreset | undefined;
  toggle_document_type_preset_create: boolean = false;
  toggle_document_type_preset: boolean = false;

  is_loading: boolean | undefined;

  async ngAfterViewInit() {
    // this.setToggleState(true, undefined, 0);
    this.status = [...await this.bs.getStatuses()];
    await this.loadPreset();
    await this.loadDocumentTypes();
  }

  async trigger() {
    this.toggle = !this.toggle;
    if (this.selection.length > 0) {
      await this.loadBatch();
    }
  }

  selectDocumentTypePreset(item: DocumentTypePreset) {
    const preset = structuredClone(item)
    this.selected_document_type_preset = preset;
    this.selected_document_types = preset.types;
    this.toggleDocumentTypePreset();
  }
  async createDocumentTypePresetLoaded(name:string | undefined){
    if(this.toggle_document_type_preset_create && name){
      const current = {id:Helpers.uuid(), name, types: structuredClone(this.selected_document_types)};
      const preset = await this.ds.addAccountPreset(current)
      current.id = preset.id;
      this.document_type_presets.push(current)
      this.selected_document_type_preset = current;
      this.toggle_document_type_preset_create = false;
      await this.loadPreset();
    }else{
      this.toggle_document_type_preset_create = !this.toggle_document_type_preset_create;
    }
  }
  async removeDocumentTypePreset(preset:DocumentTypePreset){
    if(this.selected_document_type_preset?.id == preset.id){
         this.selected_document_type_preset = undefined;
         this.selected_document_types = [];
    }
    await this.ds.removeAccountPreset(preset)
    this.toggle_document_type_preset = false;
    await this.loadPreset();
  }
  async updateDocumentTypePreset(){
    if(this.selected_document_type_preset && this.selected_document_type_preset.types){
      this.selected_document_type_preset.types = this.selected_document_types;
      await this.ds.updateAccountPreset(this.selected_document_type_preset)
      await this.loadPreset();
    }

  }

  toggleDocumentTypePreset() {
    this.toggle_document_type_preset = !this.toggle_document_type_preset;
  }

  onAccordionChange(evt: AccordionComponent) {
    if (!this.accordions || this.accordions.length == 0 || evt.id == undefined) return;
    this.setToggleState(evt.toggle, evt.id);
  }

  setToggleState(state: boolean, id: string | undefined = undefined, index: number | undefined = undefined) {
    this.accordions?.forEach((a: AccordionComponent, idx) => {
      a.toggle = false;
      if (a.id == id || idx == index) {
        a.toggle = state;
      }
    })
  }

  async onContentUpdate() {
    this.is_loading = true;

    await this.processBatchEdits();

    this.clear();

    this.is_loading = false;

    await this.trigger()
  }

  clear() {
    this.toggle_shipped = false;
    this.toggle_shipped_touched = false;
    this.toggle_document_type_preset = false;
    this.status_dropdown?.clear();
    this.status_dropdown?.trigger();
    this.eta_datepicker?.clear();
    this.pickup_toggle?.clear();

    this.document_dropdown?.forEach(dd => dd.clear());

    this.tags = [];
    this.eta = undefined;
    this.selected_status = undefined;
    this.selected_document_prefix = [];
    this.selected_document_request = [];
    this.selected_document_types = [];

  }

  onTagChange(tags: string[]) {
    this.tags = tags;
  }

  onStatusChange(status: Status) {
    this.selected_status = status;
  }

  onEtaChange(evt: OnChangePayload) {
    if (evt.type == TypeEnum.DateFilter) {
      this.eta = evt.payload['search'][0] ?? undefined;
    }
  }

  onDocumentTypeDownloadChange(documents: DocumentType[]) {
    if(documents.length){
      this.selected_document_types = documents;
    }
  }

  onDocumentRequestChange(documents: { name: string, value: AbiType }[]) {
    this.selected_document_request = documents;
  }

  onDocumentPrefixDownloadChange(prefix: { name: string, value: string }[]) {
    this.selected_document_prefix = prefix;
  }

  onPickupChange(evt: OnChangePayload) {
    if (evt.type == TypeEnum.Toggle) {
      this.toggle_shipped = evt.payload;
      this.toggle_shipped_touched = evt.payload != undefined;
    }
  }

  async onBatchExportClick() {
    if (this.selection.length > 0 && this.filter) {
      await this.bs.exportBatches(this.selection, this.filter);
      await this.trigger();
      this.clear();
    }
  }

  async getBatches() {
    if (this.selection.length > 0 && this.filter) {
      await this.bs.getBatches(this.selection, this.filter);
      await this.trigger();
      this.clear();
    }
  }

  async onPrintDocumentClick() {
    if (this.selection.length > 0 && this.selected_document_types.length > 0) {

      const queue: QueueItem = {
        data: {batches: this.selection, documents: this.selected_document_types, prefix: this.selected_document_prefix},
        type: QueueType.ExportDocumentPrints
      }

      await this.qs.queue.enqueue(async () => await this.qs.enqueue(queue));
      await this.trigger();

      this.clear();

      this.ms.set({
        message: `Batch print export are being processed`,
        type: MessageType.Success,
        delay: 2500,
      });

    } else {

      this.ms.set({
        message: `Please specify document types.`,
        type: MessageType.Info,
        delay: 2500,
      });

    }
  }

  async onDownloadDocumentClick() {
    if (this.selection.length > 0 && this.selected_document_types.length > 0) {

      const queue: QueueItem = {
        data: {
          batches: this.selection,
          documents: this.selected_document_types,
          prefix: this.selected_document_prefix,
          page: this.filter
        },
        type: QueueType.ExportDocuments
      }

      await this.qs.queue.enqueue(async () => await this.qs.enqueue(queue));
      await this.trigger();

      this.clear();

      this.ms.set({
        message: `Batch documents are being processed`,
        type: MessageType.Success,
        delay: 2500,
      });

    } else {

      this.ms.set({
        message: `Please specify document types.`,
        type: MessageType.Info,
        delay: 2500,
      });

    }
  }

  async onDocumentRequestClick() {
    if (this.selection.length > 0 && this.selected_document_request.length > 0) {

      const queue: QueueItem = {
        data: {batches: this.selection, documents: this.selected_document_request},
        type: QueueType.AbiRequest
      }

      await this.qs.queue.enqueue(async () => await this.qs.enqueue(queue));
      await this.trigger();

      this.clear();

      this.ms.set({
        message: `Batch documents have been requested.`,
        type: MessageType.Success,
        delay: 2500,
      });

    } else {

      this.ms.set({
        message: `Please specify documents to request.`,
        type: MessageType.Info,
        delay: 2500,
      });

    }
  }

  async onWorkspaceExport(page: TablePageOptions) {
    if (this.selection.length > 0) {
      const queue: QueueItem = {
        data: {batches: this.selection, page},
        type: QueueType.WorkspaceExport
      }

      await this.qs.queue.enqueue(async () => await this.qs.enqueue(queue));
      await this.trigger();

      this.clear();

      this.ms.set({
        message: `Workspace export is being processed`,
        type: MessageType.Success,
        delay: 2500,
      });
    }
  }


  async processBatchEdits() {
    const quickEditRequest: BatchQuickEdit = {
      batches: this.selection,
      tags: null,
      order_numbers: null,
      eta: null,
      status: null,
      shipped: null
    };

    if (this.selection.length == 1) {
      quickEditRequest.editType = QuickEditType.Tags;
      quickEditRequest.tags = this.tags;
      await this.qs.queue.enqueue(() => this.bs.processQuickEdit(quickEditRequest));
    }

    if (this.selected_status) {
      quickEditRequest.editType = QuickEditType.Status;
      quickEditRequest.status = this.selected_status.bsid;
      await this.qs.queue.enqueue(() => this.bs.processQuickEdit(quickEditRequest));
    }

    if (this.eta) {
      quickEditRequest.editType = QuickEditType.Eta;
      quickEditRequest.eta = this.eta;
      await this.qs.queue.enqueue(() => this.bs.processQuickEdit(quickEditRequest));
    }

    if (this.toggle_shipped_touched) {
      quickEditRequest.editType = QuickEditType.ShippingStatus;
      quickEditRequest.shipped = this.toggle_shipped;
      await this.qs.queue.enqueue(() => this.bs.processQuickEdit(quickEditRequest));
    }

    this.change.emit({type: OnChangeType.Reload});
  }

  async loadPreset(){
    const res = await this.ds.getAccountPresets();
    this.document_type_presets = res.presets ?? [];
  }

  async loadDocumentTypes() {
    const res = await this.ds.getDocumentTypes();
    this.document_types = res[0].types ?? [];
  }

  async loadBatch() {
    if (this.toggle) {
      this.tags = [];
      if (this.selection.length == 1) {
        const res = await this.bs.getBatch(this.selection[0]);
        if (res && res.tags) {
          this.tags = (typeof res.tags == "string") ? JSON.parse(res.tags) : res.tags;
        }
      }
    }
  }


  protected readonly Role = Role;
}
